<template>
  <div class="accordion">
    <div
      v-for="panel, index of panels"
      :key="index"
      class="accordion__item"
      :class="[
        $blockClass(blockClass, 'accordion-item'),
      ]"
    >
      <button
        type="button"
        class="accordion__trigger"
        :class="[
          $blockClass(blockClass, 'trigger'),
          { [$class('active')]: panel.active }
        ]"
        :aria-expanded="panel.active"
        @click="togglePanel(index)"
      >
        <component
          :is="headingType"
          class="accordion__heading"
          :class="titleClass"
        >
          {{ panel.title }}
        </component>

        <icon-minus v-if="panel.active" />
        <icon-plus v-else />
      </button>

      <transition-height>
        <div
          v-if="panel.active"
          class="accordion__content"
          v-html="panel.content"
        />
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion (accordion)
 * -----------------------------------------------------------------------------
 * Renders a set of accordion panels that can be opened and closed.
 *
 * @param {String} [blockClass] - Block class to apply to elements.
 * @param {String} headingType - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {String} panelData.content - Panel content.
 * @param {String} panelData.title - Title of the panel, used as a label for the
 *   toggle button.
 * @param {Boolean} singleToggle - True if only one is open at a time.
 * @param {String} [titleClass] - Space separated list of additional title
 * classes.
 */
import { mapState } from 'vuex'

import IconPlus from '~icons/directional-navigation/plus.svg'
import IconMinus from '~icons/directional-navigation/minus.svg'
import TransitionHeight from '~async/transition-height/transition-height'

export default {
  name: 'AccordionSnippet',

  components: {
    IconPlus,
    IconMinus,
    TransitionHeight,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'span',
    },
    panelData: {
      type: Array,
      default: () => [],
    },
    singleToggle: {
      type: Boolean,
      default: false,
    },
    titleClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      panels: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.setPanels()
  },

  methods: {

    /**
     * Store panels on the component with a toggleable 'active' property.
     */
    setPanels() {
      this.panels = this.panelData.map((panel) => {
        return {
          active: false,
          ...panel,
        }
      })
    },

    /**
     * Toggle a panel's active state.
     * - On mobile viewports, panels are toggled independently.
     * - On non-mobile viewports, opening one panel will close all other panels.
     * @param {Number} targetIndex - Index of a specific panel.
     */
    togglePanel(targetIndex) {
      const targetPanel = this.panels[targetIndex]
      targetPanel.active = !targetPanel.active

      if (this.singleToggle && this.isMobile) {
        this.panels.forEach((panel, index) => {
          if (index === targetIndex) {
            return
          }

          panel.active = false
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import './accordion-snippet';
</style>
